// @file Grade-band definitions and selection helpers for the dashboard template gallery filter.
// Presentation-only — holds no reactive state and makes no store edits. Bands group the raw grade
// keys served by the gallery API into display ranges (K - 5, 6 - 8, ...). Selecting a band selects
// every raw member key, and the OR-based grade filter then matches any template tagged with those keys.
import { getCurrentCountryIso31661, getHyphenatedCurrentLocale } from '@@/bits/current_locale'
import { HIGHER_EDUCATION_GRADE_KEY } from '@@/bits/dash_gallery_helper'
import { p__, __ } from '@@/bits/intl'

// No raw grade key exists for adults in the current data; reserved for future Airtable tagging,
// so the band stays hidden (see visibleGradeBands) until a template is tagged with it.
const ADULTS_GRADE_KEY = 'Adults'

// Countries whose school systems label class levels "Year" (Year 1, Year 2…) rather than "Grade".
// Matches the only two locales Arcade actually renders with "Year" grade labels (en-GB, en-AU in
// sondheim communityLocaleData GRADE_MAPPINGS). We key off the detected country (data-country,
// CloudFlare geo) rather than the locale because en-AU is not a supported Padlet locale — an
// Australian user's locale falls back to plain `en`, so a locale-only check never fires for them.
const YEAR_TERMINOLOGY_COUNTRIES = ['GB', 'AU']

interface GradeBand {
  id: string
  memberKeys: string[]
  label: string
}

// Built lazily (not a module constant) so gettext labels resolve against the active locale,
// which may be set after this module is first imported.
function getGradeBands(): GradeBand[] {
  // Numeric ranges are locale-agnostic; only the word labels need translation.
  const kLabel = p__('Short for Kindergarten, as a school class level.', 'K')
  return [
    { id: 'k_5', memberKeys: ['K', '1', '2', '3', '4', '5'], label: `${kLabel} - 5` },
    { id: '6_8', memberKeys: ['6', '7', '8'], label: '6 - 8' },
    { id: '9_12', memberKeys: ['9', '10', '11', '12'], label: '9 - 12' },
    {
      id: 'higher_ed',
      memberKeys: [HIGHER_EDUCATION_GRADE_KEY],
      label: p__('A school class level: tertiary / college education.', 'Higher Education'),
    },
    { id: 'adults', memberKeys: [ADULTS_GRADE_KEY], label: __('Adults') },
  ]
}

// availableGrades is the set of grade keys templates in the gallery are actually tagged with —
// not every possible member key — so a band's member keys are intersected against it.
function presentMemberKeys(band: GradeBand, availableGrades: string[]): string[] {
  return band.memberKeys.filter((key) => availableGrades.includes(key))
}

function visibleGradeBands(availableGrades: string[]): GradeBand[] {
  return getGradeBands().filter((band) => presentMemberKeys(band, availableGrades).length > 0)
}

function isBandSelected(
  band: GradeBand,
  availableGrades: string[],
  gradesSelectedMap: Record<string, boolean>,
): boolean {
  const present = presentMemberKeys(band, availableGrades)
  return present.length > 0 && present.every((key) => gradesSelectedMap[key])
}

// Computes which keys to pass to updateSelectedGrades() to toggle a band.
// If not fully selected, returns unselected members (to select them).
// If fully selected, returns all present members (to deselect them).
function getBandToggleKeys(
  band: GradeBand,
  availableGrades: string[],
  gradesSelectedMap: Record<string, boolean>,
): string[] {
  const present = presentMemberKeys(band, availableGrades)
  if (present.length === 0) return []
  const fullySelected = present.every((key) => gradesSelectedMap[key])
  return fullySelected ? present : present.filter((key) => !gradesSelectedMap[key])
}

// Keys to pass to updateSelectedGrades for a group-level "Select all": every present band member
// not already selected.
function getAllUnselectedKeys(availableGrades: string[], gradesSelectedMap: Record<string, boolean>): string[] {
  return visibleGradeBands(availableGrades)
    .flatMap((band) => presentMemberKeys(band, availableGrades))
    .filter((key) => !gradesSelectedMap[key])
}

// Users can only pick whole bands, and a band shows as selected only when all its present grades are.
// Select the grade's whole band (2 → K to 5). Grades in no band are kept as they are.
function expandGradesToBands(gradeKeys: string[], availableGrades: string[]): string[] {
  const bands = getGradeBands()
  const banded = bands
    .filter((band) => band.memberKeys.some((key) => gradeKeys.includes(key)))
    .flatMap((band) => presentMemberKeys(band, availableGrades))
  const unbanded = gradeKeys.filter((key) => !bands.some((band) => band.memberKeys.includes(key)))
  return [...banded, ...unbanded]
}

// The numeric band ranges read the same everywhere, so only the heading word is localized:
// "Year" for the UK/Australia/etc., "Grade" (translated per language) elsewhere. The explicit
// en-GB locale check is OR'd in so a user who deliberately picks English (UK) still sees "Year",
// matching Arcade.
function getGradeFilterHeadingLabel(): string {
  const usesYearTerminology =
    getHyphenatedCurrentLocale() === 'en-GB' || YEAR_TERMINOLOGY_COUNTRIES.includes(getCurrentCountryIso31661())
  return usesYearTerminology
    ? p__('School class levels in the UK/Australia. For example: Year 6.', 'Year')
    : p__('Refers to school class levels. For example: kindergarten, grade 6.', 'Grade')
}

export type { GradeBand }
export {
  expandGradesToBands,
  getAllUnselectedKeys,
  getBandToggleKeys,
  getGradeBands,
  getGradeFilterHeadingLabel,
  isBandSelected,
  presentMemberKeys,
  visibleGradeBands,
}
